import { useState } from 'react'
import { supabase } from './supabaseClient'

// ============================================
// CONFIG — tweak everything here
// ============================================
const CONFIG = {
  // ---- BRAND & COPY ----
  appName: 'AirLog',
  tagline: 'A permanent second memory across all devices. Developed by Algorithmic Enterprise.',
  iconSrc: '/airlog-icon.png',
  termsText: 'Terms & Conditions  |  Privacy Policy',

  // ---- COLORS ----
  pageBg: '#000000',
  surfaceBg: '#1C1C1E',
  surfaceBorder: 'rgba(255, 255, 255, 0.06)',
  textPrimary: '#FFFFFF',
  textSecondary: '#8E8E93',
  textTertiary: '#6E6E73',
  accent: '#007AFF',
  accentPressed: '#0066CC',
  fieldGroupBg: 'rgba(58, 58, 60, 0.55)',
  fieldDivider: 'rgba(255, 255, 255, 0.08)',
  fieldPlaceholder: '#8E8E93',

  // ---- MOBILE (≤768px) — sheet style ----
  mobile: {
    sheetMinHeight: '72vh',      // how tall the bottom sheet is
    sheetTopRadius: 28,          // rounded top corners only
    paddingX: 28,
    paddingTop: 40,
    paddingBottom: 32,           // safe-area-inset auto-added on top of this
    iconSize: 120,
    welcomeTitleSize: 38,
    welcomeTaglineSize: 13,
    titleSize: 34,
    subtitleSize: 15,
  },

  // ---- DESKTOP (>768px) — centered card ----
  desktop: {
    cardWidth: 440,
    cardHeight: 600,
    cardRadius: 28,
    cardPadding: 48,
    iconSize: 140,
    welcomeTitleSize: 44,
    welcomeTaglineSize: 14,
    titleSize: 40,
    subtitleSize: 16,
  },

  // ---- FIELDS (iOS grouped-list style) ----
  fieldGroupRadius: 14,
  fieldPaddingY: 16,
  fieldPaddingX: 18,
  fieldFontSize: 16,

  // ---- BUTTON ----
  buttonHeight: 52,
  buttonRadius: 14,
  buttonFontSize: 17,
  buttonFontWeight: 400,

  // ---- TRANSITIONS ----
  screenFadeMs: 240,
}

export default function Auth() {
  const [screen, setScreen] = useState('welcome') // 'welcome' | 'signup' | 'signin'
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const handleAuth = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      if (screen === 'signup') {
        const { error } = await supabase.auth.signUp({ email, password })
        if (error) throw error
        alert('Check your email for the confirmation link!')
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      }
    } catch (error) {
      alert(error.message)
    } finally {
      setLoading(false)
    }
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      <style>{`
        .auth-page {
          min-height: 100vh;
          background: ${CONFIG.pageBg};
          font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', sans-serif;
          display: flex;
          color: ${CONFIG.textPrimary};
          -webkit-font-smoothing: antialiased;
        }

        /* ---------- MOBILE: bottom sheet ---------- */
        @media (max-width: 768px) {
          .auth-page {
            align-items: flex-end;
            justify-content: stretch;
          }
          .auth-surface {
            width: 100%;
            background: ${CONFIG.surfaceBg};
            border-top-left-radius: ${CONFIG.mobile.sheetTopRadius}px;
            border-top-right-radius: ${CONFIG.mobile.sheetTopRadius}px;
            min-height: ${CONFIG.mobile.sheetMinHeight};
            padding:
              ${CONFIG.mobile.paddingTop}px
              ${CONFIG.mobile.paddingX}px
              calc(${CONFIG.mobile.paddingBottom}px + env(safe-area-inset-bottom));
            display: flex;
            flex-direction: column;
            border-top: 1px solid ${CONFIG.surfaceBorder};
          }
          .auth-title-welcome { font-size: ${CONFIG.mobile.welcomeTitleSize}px; }
          .auth-tagline       { font-size: ${CONFIG.mobile.welcomeTaglineSize}px; }
          .auth-title-form    { font-size: ${CONFIG.mobile.titleSize}px; }
          .auth-subtitle      { font-size: ${CONFIG.mobile.subtitleSize}px; }
          .auth-icon          { width: ${CONFIG.mobile.iconSize}px; height: ${CONFIG.mobile.iconSize}px; }
        }

        /* ---------- DESKTOP: centered card ---------- */
        @media (min-width: 769px) {
          .auth-page {
            align-items: center;
            justify-content: center;
            padding: 24px;
          }
          .auth-surface {
            background: ${CONFIG.surfaceBg};
            border-radius: ${CONFIG.desktop.cardRadius}px;
            width: 100%;
            max-width: ${CONFIG.desktop.cardWidth}px;
            min-height: ${CONFIG.desktop.cardHeight}px;
            padding: ${CONFIG.desktop.cardPadding}px;
            display: flex;
            flex-direction: column;
            border: 1px solid ${CONFIG.surfaceBorder};
          }
          .auth-title-welcome { font-size: ${CONFIG.desktop.welcomeTitleSize}px; }
          .auth-tagline       { font-size: ${CONFIG.desktop.welcomeTaglineSize}px; }
          .auth-title-form    { font-size: ${CONFIG.desktop.titleSize}px; }
          .auth-subtitle      { font-size: ${CONFIG.desktop.subtitleSize}px; }
          .auth-icon          { width: ${CONFIG.desktop.iconSize}px; height: ${CONFIG.desktop.iconSize}px; }
        }

        /* ---------- Shared ---------- */
        .auth-screen {
          flex: 1;
          display: flex;
          flex-direction: column;
          animation: authFade ${CONFIG.screenFadeMs}ms ease-out;
        }
        @keyframes authFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* Welcome screen */
        .welcome-top {
          text-align: center;
          padding-top: 8px;
        }
        .auth-title-welcome {
          font-weight: 700;
          letter-spacing: -0.5px;
          margin: 0 0 10px 0;
        }
        .auth-tagline {
          color: ${CONFIG.textSecondary};
          line-height: 1.45;
          margin: 0 auto;
          max-width: 320px;
        }
        .welcome-icon-wrap {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 0;
        }
        .auth-icon {
         display: block;
        object-fit: contain;
        filter: drop-shadow(0 18px 40px rgba(0, 122, 255, 0.25));
        }
        .welcome-bottom {
          display: flex;
          flex-direction: column;
          gap: 18px;
          align-items: center;
        }
        .terms-text {
          color: ${CONFIG.accent};
          font-size: 13px;
          text-align: center;
          opacity: 0.9;
          cursor: pointer;
        }

        /* Form screens */
        .form-top {
          display: flex;
          flex-direction: column;
        }
        .auth-title-form {
          font-weight: 700;
          letter-spacing: -0.5px;
          margin: 0 0 6px 0;
        }
        .auth-subtitle {
          color: ${CONFIG.textSecondary};
          margin: 0 0 28px 0;
        }

        /* Grouped fields */
        .field-group {
          background: ${CONFIG.fieldGroupBg};
          border-radius: ${CONFIG.fieldGroupRadius}px;
          overflow: hidden;
        }
        .field {
          width: 100%;
          background: transparent;
          border: none;
          outline: none;
          padding: ${CONFIG.fieldPaddingY}px ${CONFIG.fieldPaddingX}px;
          color: ${CONFIG.textPrimary};
          font-size: ${CONFIG.fieldFontSize}px;
          font-family: inherit;
          box-sizing: border-box;
          -webkit-appearance: none;
        }
        .field::placeholder { color: ${CONFIG.fieldPlaceholder}; }
        .field-divider {
         height: 1px;
         background: ${CONFIG.fieldDivider};
         margin-left: ${CONFIG.fieldPaddingX}px;
         margin-right: ${CONFIG.fieldPaddingX}px;
        }

        /* Bottom group: button + toggle */
        .form-bottom {
          margin-top: auto;
          padding-top: 28px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .primary-btn {
          height: ${CONFIG.buttonHeight}px;
          width: 100%;
          background: ${CONFIG.accent};
          color: #fff;
          border: none;
          border-radius: ${CONFIG.buttonRadius}px;
          font-size: ${CONFIG.buttonFontSize}px;
          font-weight: ${CONFIG.buttonFontWeight};
          font-family: inherit;
          cursor: pointer;
          transition: transform 0.12s ease, background 0.12s ease, opacity 0.12s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .primary-btn:active:not(:disabled) {
          background: ${CONFIG.accentPressed};
          transform: scale(0.985);
        }
        .primary-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .toggle-link {
          background: none;
          border: none;
          color: ${CONFIG.accent};
          font-size: 14px;
          font-family: inherit;
          cursor: pointer;
          text-align: center;
          padding: 6px;
        }
      `}</style>

      <div className="auth-page">
        <div className="auth-surface">

          {/* ---------------- WELCOME ---------------- */}
          {screen === 'welcome' && (
            <div className="auth-screen" key="welcome">
              <div className="welcome-top">
                <h1 className="auth-title-welcome">{CONFIG.appName}</h1>
                <p className="auth-tagline">{CONFIG.tagline}</p>
              </div>

              <div className="welcome-icon-wrap">
                <img src={CONFIG.iconSrc} alt={`${CONFIG.appName} icon`} className="auth-icon" />
              </div>

              <div className="welcome-bottom">
                <button
                  className="primary-btn"
                  onClick={() => setScreen('signup')}
                >
                  Join {CONFIG.appName}
                </button>
                <div className="terms-text">{CONFIG.termsText}</div>
              </div>
            </div>
          )}

          {/* ---------------- SIGN UP ---------------- */}
          {screen === 'signup' && (
            <div className="auth-screen" key="signup">
              <div className="form-top">
                <h1 className="auth-title-form">Create Account</h1>
                <p className="auth-subtitle">Sign Up for {CONFIG.appName}</p>
              </div>

              <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div className="field-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="field"
                  />
                  <div className="field-divider" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    className="field"
                  />
                </div>

                <div className="form-bottom">
                  <button type="submit" disabled={loading} className="primary-btn">
                    {loading ? 'Signing up…' : 'Sign Up'}
                  </button>
                  <button type="button" className="toggle-link" onClick={() => setScreen('signin')}>
                    Already have an account? Sign in
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ---------------- SIGN IN ---------------- */}
          {screen === 'signin' && (
            <div className="auth-screen" key="signin">
              <div className="form-top">
                <h1 className="auth-title-form">Welcome Back</h1>
                <p className="auth-subtitle">Sign In for {CONFIG.appName}</p>
              </div>

              <form onSubmit={handleAuth} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <div className="field-group">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="field"
                  />
                  <div className="field-divider" />
                  <input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="field"
                  />
                </div>

                <div className="form-bottom">
                  <button type="submit" disabled={loading} className="primary-btn">
                    {loading ? 'Signing in…' : 'Sign In'}
                  </button>
                  <button type="button" className="toggle-link" onClick={() => setScreen('signup')}>
                    Don't have an account? Sign up
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      </div>
    </>
  )
}